import type { ComponentType, ReactNode } from 'react'
import type { IconGlyphProps } from './icon-registry'

/**
 * Local, vendored stroke-icon glyphs (UID-136 "migrate" phase, ADR-0054).
 *
 * The design source (`Fictiv Platform.dc.html`) draws every icon as a plain
 * inline SVG in a single consistent language — `viewBox="0 0 24 24"`,
 * `fill="none"`, `stroke="currentColor"`, `stroke-width="2"`,
 * `stroke-linecap`/`stroke-linejoin="round"` (the open Feather/Lucide stroke
 * icon convention). That language is visibly different from
 * `@ant-design/icons`' filled icon-font glyphs — different stroke weight, no
 * shared corner-radius language, and in several cases an entirely different
 * pictogram (e.g. the sidebar-collapse control, the "search catalog" nav
 * item, the Orders nav item, the Share icon). Since this sandbox cannot run
 * `npm install` (no registry network — see the UID-136 feature note), the
 * path data below is copied byte-for-byte from the design source rather than
 * pulled from an icon-library package; there is no new dependency.
 *
 * Each glyph reproduces `@ant-design/icons`' own render contract — an outer
 * element that receives `className`/`style`/`title`/the a11y props from
 * `Icon.tsx`, wrapping an inner `1em`-square `<svg>` — so swapping a
 * `icon-registry.ts` entry from an Ant component to one of these is a true
 * drop-in with no change to `Icon.tsx`'s sizing logic.
 */
function createGlyph(children: ReactNode, strokeWidth = 2): ComponentType<IconGlyphProps> {
  function Glyph({ className, style, title, ...a11y }: IconGlyphProps) {
    return (
      <span className={className} style={style} title={title} {...a11y}>
        <svg
          width="1em"
          height="1em"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          // The accessible name (if any) always lives on the OUTER `<span>`
          // above, via `Icon.tsx`'s `role="img"`/`aria-label` contract — this
          // raw SVG shape itself is never the accessible-name carrier.
          aria-hidden="true"
        >
          {children}
        </svg>
      </span>
    )
  }
  return Glyph
}

/** Global shell — Sidebar.tsx */

export const HouseGlyph = createGlyph(
  <>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </>,
)

export const LayoutGridGlyph = createGlyph(
  <>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </>,
)

/** Sidebar "Part library" nav item — a distinct pictogram from the RFQ
 * file-text icon below; the app previously (incorrectly) reused ONE icon
 * for both nav items. */
export const BoxGlyph = createGlyph(
  <>
    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="m3.3 7 8.7 5 8.7-5" />
    <path d="M12 22V12" />
  </>,
)

export const FileTextGlyph = createGlyph(
  <>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M9 15h6M9 11h6" />
  </>,
)

export const PackageCheckGlyph = createGlyph(
  <>
    <rect width="8" height="4" x="8" y="2" rx="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="m9 14 2 2 4-4" />
  </>,
)

export const PlusGlyph = createGlyph(
  <>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </>,
  2.5,
)

/** Sidebar collapse/expand — the SAME pictogram, `Icon.tsx`/CSS handles the
 * 180deg rotation between the two states exactly like the design source
 * does (`transform:rotate(180deg)` on the expand control). */
export const PanelLeftGlyph = createGlyph(
  <>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
    <path d="m16 15-3-3 3-3" />
  </>,
)

/** TopBar mobile nav toggle (the ≤900px off-canvas-drawer hamburger). Not in
 * the desktop design — the shell is always-expanded there — so this is the one
 * shell glyph without a design-source counterpart; drawn in the same stroke
 * language as the rest. */
export const MenuGlyph = createGlyph(
  <>
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </>,
)

/** Shared shell + topbar */

export const SearchGlyph = createGlyph(
  <>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </>,
)

export const ShoppingCartGlyph = createGlyph(
  <>
    <circle cx="8" cy="21" r="1" />
    <circle cx="19" cy="21" r="1" />
    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
  </>,
)

export const ChevronDownGlyph = createGlyph(<polyline points="6 9 12 15 18 9" />, 2.2)

/** RFQ vertical */

export const DownloadGlyph = createGlyph(
  <>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </>,
)

/** TopBar (UID-244, 2026-08-23 design refresh) — the mirror of DownloadGlyph
 * above. Path data copied byte-for-byte from the design source's own top-bar
 * "Upload" button (`Fictiv Platform.dc.html:366`, `startStaging` handler). */
export const UploadGlyph = createGlyph(
  <>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </>,
)

/** UID-265b (ADR-0093 D6) — the RFQ detail rail's "Edit RFQ" control. Path
 * data copied byte-for-byte from the design source's own button
 * (`Fictiv Platform.dc.html:3725`): the Lucide/Feather "square-pen" glyph.
 * NOT `@ant-design/icons`' `EditOutlined`, which is a filled-font glyph in a
 * visibly different icon language (the mismatch ADR-0054's migrate phase
 * exists to remove). */
export const SquarePenGlyph = createGlyph(
  <>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </>,
)

export const LockGlyph = createGlyph(
  <>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </>,
)

export const ShareGlyph = createGlyph(
  <>
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
    <line x1="15.4" y1="6.5" x2="8.6" y2="10.5" />
  </>,
)

/** Account menu "Log out" (UID-301) — lucide log-out glyph, styled red at the
 * call site. Path data copied byte-for-byte from the design source
 * (`Fictiv Platform.dc.html` account menu). */
export const LogOutGlyph = createGlyph(
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" x2="9" y1="12" y2="12" />
  </>,
)

/** Account menu "Account settings" (UID-343). Path data copied byte-for-byte
 * from the design source's account-menu gear glyph (`Fictiv Platform.dc.html`
 * account menu, `onAcctSettings` row). */
export const SettingsGlyph = createGlyph(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </>,
)

/** TopBar search-field filter control (UID-301). The design draws a
 * "sliders" adjust glyph, NOT a funnel — path data copied byte-for-byte from
 * the design source (`Fictiv Platform.dc.html:316`). Replaces the Ant
 * `FilterOutlined` the registry previously kept as a stopgap. */
export const SlidersGlyph = createGlyph(
  <>
    <line x1="4" x2="4" y1="21" y2="14" />
    <line x1="4" x2="4" y1="10" y2="3" />
    <line x1="12" x2="12" y1="21" y2="12" />
    <line x1="12" x2="12" y1="8" y2="3" />
    <line x1="20" x2="20" y1="21" y2="16" />
    <line x1="20" x2="20" y1="12" y2="3" />
    <line x1="1" x2="7" y1="14" y2="14" />
    <line x1="9" x2="15" y1="8" y2="8" />
    <line x1="17" x2="23" y1="16" y2="16" />
  </>,
)

/** Sidebar help card — the copy-to-clipboard affordance next to the
 * sales@/support@ mailto links. Path data copied byte-for-byte from the design
 * source's own copy glyph (`Fictiv Platform.dc.html:289`, `copySales`). */
export const CopyGlyph = createGlyph(
  <>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </>,
)

export const ClockGlyph = createGlyph(
  <>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </>,
)

export const CheckGlyph = createGlyph(<polyline points="20 6 9 17 4 12" />, 3.5)

export const CheckCircleGlyph = createGlyph(
  <>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </>,
)

/** Design source: an alert TRIANGLE, not Ant's exclamation-CIRCLE — a real
 * pictogram change, not just a stroke-weight difference. */
export const AlertTriangleGlyph = createGlyph(
  <>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </>,
)

/** UID-359 — the RFQ compose parts-row "⋯" menu (`Fictiv Platform.dc.html:4235`
 * "Move to a new RFQ"). Path data copied byte-for-byte from the design source.
 * Distinct from `ShareGlyph`: this is the arrow-out-of-a-box "opens elsewhere"
 * pictogram, not the three-node share graph. */
export const ExternalLinkGlyph = createGlyph(
  <path d="M17 3h4v4M14 10l7-7M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />,
)

/** UID-359 — the same row menu's destructive "Remove from request"
 * (`Fictiv Platform.dc.html:4236`). Path data copied byte-for-byte. */
export const Trash2Glyph = createGlyph(
  <>
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </>,
)

/** UID-359 — the parts-row "Needs quote" pill's leading glyph
 * (`Fictiv Platform.dc.html:4225`). A PLAIN document outline: deliberately NOT
 * `FileTextGlyph` above, which adds two body text-rules the design's pill does
 * not draw. Path data copied byte-for-byte. */
export const FileGlyph = createGlyph(
  <>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </>,
)
